import React, { useState } from 'react';
import { Github, Linkedin, Twitter, Instagram, Heart, ArrowRight, Mail, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Logo } from './Logo';
import { Link } from 'react-router-dom';

export const Footer: React.FC = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');

  return (
    <footer className="mt-20">
      {/* Primary CTA Banner - Coffee Chat conversion */}
      <div className="bg-foreground text-background py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-semibold mb-2">Let's Chat Over Coffee ☕</h3>
              <p className="text-background/70">15-min call to discuss your project. Just <span className="line-through opacity-70">$9.99</span> <span className="font-bold">$4.99</span> — 50% off!</p>
            </div>
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background text-foreground rounded-lg font-medium hover:opacity-90 transition-opacity whitespace-nowrap group"
            >
              <Calendar className="w-4 h-4" />
              Book Coffee Chat
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Newsletter Section - Lead capture */}
      <div className="bg-muted/50 py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-foreground/10 rounded-lg">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-medium">Get Free Tools & Tips</h4>
                <p className="text-sm text-muted-foreground">Weekly insights on e-commerce & tech. Unsubscribe anytime.</p>
              </div>
            </div>
            <form className="flex gap-2 w-full md:w-auto" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 md:w-64 px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
              />
              <button 
                type="submit"
                className="px-5 py-2.5 bg-foreground text-background rounded-lg font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="border-t border-border py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            {/* Brand Column */}
            <div className="md:col-span-5">
              <Logo size="lg" />
              <p className="text-muted-foreground text-sm max-w-sm leading-relaxed mb-4">
                {t('hero', 'subtitle')}
              </p>
              {/* Social Proof */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="font-semibold text-foreground">50+</span> Projects Delivered
                </span>
                <span className="flex items-center gap-1">
                  <span className="font-semibold text-foreground">5★</span> Client Rating
                </span>
              </div>
            </div>

            {/* Services - Primary conversion path */}
            <div className="md:col-span-3">
              <h4 className="font-medium text-sm mb-4">Work With Me</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/consultation" className="text-muted-foreground hover:text-foreground transition-colors text-sm flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Book Consultation
                  </Link>
                </li>
                <li>
                  <Link to="/free-tools" className="text-muted-foreground hover:text-foreground transition-colors text-sm flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Free Tools
                  </Link>
                </li>
                <li>
                  <Link to="/store" className="text-muted-foreground hover:text-foreground transition-colors text-sm flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    Courses & Guides
                  </Link>
                </li>
              </ul>
            </div>

            {/* Learn - Secondary engagement */}
            <div className="md:col-span-2">
              <h4 className="font-medium text-sm mb-4">Learn</h4>
              <ul className="space-y-2.5">
                <li>
                  <a href="https://blog.cemates.me" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                    Blog
                  </a>
                </li>
                <li>
                  <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Connect - Social & Trust */}
            <div className="md:col-span-2">
              <h4 className="font-medium text-sm mb-4">Connect</h4>
              <div className="flex gap-3">
                <a
                  href="https://github.com/cemates"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="GitHub"
                >
                  <Github className="w-4 h-4" />
                </a>
                <a
                  href="https://www.linkedin.com/in/cematesh/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a
                  href="https://x.com/cematesh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href="https://instagram.com/cemates"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-10 pt-6 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-muted-foreground text-sm flex items-center">
                {t('footer', 'madeWith')} <Heart className="w-3 h-3 mx-1 text-red-500" /> © {year} cemates.me
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-xs text-muted-foreground">
                <Link to="/privacy" className="hover:text-foreground transition-colors">
                  {t('footer', 'privacy')}
                </Link>
                <Link to="/terms" className="hover:text-foreground transition-colors">
                  {t('footer', 'terms')}
                </Link>
                <Link to="/cookies" className="hover:text-foreground transition-colors">
                  {t('footer', 'cookies')}
                </Link>
                <Link to="/disclaimer" className="hover:text-foreground transition-colors">
                  Disclaimer
                </Link>
                <Link to="/sitemap" className="hover:text-foreground transition-colors">
                  Sitemap
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
